import fabric from '@/lib/fabric';

export const CROP_OVERLAY_KEY = 'craftaraCropOverlay';

export interface ImageCropSnapshot {
  cropX: number;
  cropY: number;
  width: number;
  height: number;
  left: number;
  top: number;
  scaleX: number;
  scaleY: number;
  angle: number;
}

export interface ImageCropSession {
  mode: 'image';
  target: fabric.Image;
  overlay: fabric.Rect;
  snapshot: ImageCropSnapshot;
}

export interface ArtboardCropSession {
  mode: 'artboard';
  overlay: fabric.Rect;
}

export type CropSession = ImageCropSession | ArtboardCropSession;

function markOverlay(rect: fabric.Rect): fabric.Rect {
  (rect as fabric.Rect & { [CROP_OVERLAY_KEY]?: boolean })[CROP_OVERLAY_KEY] = true;
  return rect;
}

export function isCropOverlay(obj: fabric.Object | null | undefined): boolean {
  return Boolean(obj && (obj as unknown as Record<string, unknown>)[CROP_OVERLAY_KEY]);
}

export function createCropOverlay(bounds: {
  left: number;
  top: number;
  width: number;
  height: number;
}): fabric.Rect {
  const rect = new fabric.Rect({
    left: bounds.left,
    top: bounds.top,
    width: Math.max(8, bounds.width),
    height: Math.max(8, bounds.height),
    fill: 'rgba(13, 115, 119, 0.12)',
    stroke: '#0d7377',
    strokeWidth: 1,
    strokeDashArray: [6, 4],
    selectable: true,
    evented: true,
    hasRotatingPoint: false,
    lockRotation: true,
    objectCaching: false,
    excludeFromExport: true,
  });
  return markOverlay(rect);
}

export function beginImageCrop(
  canvas: fabric.Canvas,
  image: fabric.Image
): ImageCropSession {
  const bound = image.getBoundingRect(true);
  const overlay = createCropOverlay({
    left: bound.left,
    top: bound.top,
    width: bound.width,
    height: bound.height,
  });

  const snapshot: ImageCropSnapshot = {
    cropX: image.cropX || 0,
    cropY: image.cropY || 0,
    width: image.width || bound.width,
    height: image.height || bound.height,
    left: image.left || 0,
    top: image.top || 0,
    scaleX: image.scaleX || 1,
    scaleY: image.scaleY || 1,
    angle: image.angle || 0,
  };

  image.set({ selectable: false, evented: false });
  canvas.add(overlay);
  canvas.setActiveObject(overlay);
  canvas.requestRenderAll();

  return { mode: 'image', target: image, overlay, snapshot };
}

/** Apply overlay bounds as Fabric cropX/cropY/width/height (axis-aligned images). */
export function applyImageCrop(session: ImageCropSession): boolean {
  const { target, overlay, snapshot } = session;
  if (Math.abs(snapshot.angle) > 0.5) {
    // Rotated images: keep overlay as clipPath instead of native crop fields
    const w = Math.max(8, (overlay.width || 0) * (overlay.scaleX || 1));
    const h = Math.max(8, (overlay.height || 0) * (overlay.scaleY || 1));
    const clip = new fabric.Rect({
      left: overlay.left,
      top: overlay.top,
      width: w,
      height: h,
      absolutePositioned: true,
    });
    target.set({ clipPath: clip, selectable: true, evented: true });
    return true;
  }

  const scaleX = snapshot.scaleX || 1;
  const scaleY = snapshot.scaleY || 1;
  const oLeft = overlay.left || 0;
  const oTop = overlay.top || 0;
  const oW = Math.max(8, (overlay.width || 0) * (overlay.scaleX || 1));
  const oH = Math.max(8, (overlay.height || 0) * (overlay.scaleY || 1));

  const relX = (oLeft - snapshot.left) / scaleX;
  const relY = (oTop - snapshot.top) / scaleY;
  const relW = oW / scaleX;
  const relH = oH / scaleY;

  const nextCropX = Math.max(0, snapshot.cropX + relX);
  const nextCropY = Math.max(0, snapshot.cropY + relY);

  target.set({
    left: oLeft,
    top: oTop,
    cropX: nextCropX,
    cropY: nextCropY,
    width: Math.max(1, relW),
    height: Math.max(1, relH),
    scaleX,
    scaleY,
    selectable: true,
    evented: true,
    dirty: true,
  });
  target.setCoords();
  return true;
}

export function cancelImageCrop(session: ImageCropSession): void {
  const { target, snapshot } = session;
  target.set({
    cropX: snapshot.cropX,
    cropY: snapshot.cropY,
    width: snapshot.width,
    height: snapshot.height,
    left: snapshot.left,
    top: snapshot.top,
    scaleX: snapshot.scaleX,
    scaleY: snapshot.scaleY,
    selectable: true,
    evented: true,
  });
  target.setCoords();
}

export function applyArtboardCrop(
  canvas: fabric.Canvas,
  overlay: fabric.Rect
): { width: number; height: number } {
  const left = overlay.left || 0;
  const top = overlay.top || 0;
  const width = Math.max(32, Math.round((overlay.width || 0) * (overlay.scaleX || 1)));
  const height = Math.max(32, Math.round((overlay.height || 0) * (overlay.scaleY || 1)));

  const objects = canvas.getObjects().filter((obj) => !isCropOverlay(obj));
  objects.forEach((obj) => {
    const b = obj.getBoundingRect(true);
    const intersects =
      b.left < left + width &&
      b.left + b.width > left &&
      b.top < top + height &&
      b.top + b.height > top;
    if (!intersects) {
      canvas.remove(obj);
      return;
    }
    obj.set({
      left: (obj.left || 0) - left,
      top: (obj.top || 0) - top,
    });
    obj.setCoords();
  });

  canvas.setWidth(width);
  canvas.setHeight(height);
  canvas.calcOffset();
  canvas.requestRenderAll();
  return { width, height };
}

export function removeCropOverlay(canvas: fabric.Canvas, overlay: fabric.Rect): void {
  canvas.remove(overlay);
  canvas.discardActiveObject();
  canvas.requestRenderAll();
}
